import { Query, query } from "@archipad-js/core/orm";
import { createEntityWithProperties, UpdateEntityPayload, updateEntityWithProperties } from "@archipad-js/core/orm";

import {
    MAP_TYPES,
    ProjectEntity,
    RegionEntity,
    RegionMemberEntity,
    RegionTypeEntity,
} from "@archipad-js/archipad-model";

export interface RegionTypePayload {
    id?: string;
    name: string;
    readonly isSystem?: boolean;
}

export class RegionService {
    public listRegions(where: {
        project: ProjectEntity;
        ignoreNullRegions?: boolean;
    }): [null, ...RegionEntity[]] | Array<RegionEntity> {
        const query = this.makeListRegionQuery(where);
        const resultSet = query.execute();
        const regions = resultSet.sortedEntities();
        return where.ignoreNullRegions ? regions : [null, ...regions];
    }

    public listRegionsByTypes(where: {
        project: ProjectEntity;
        /**
         * Only the region members of the given regionTypes will be returned,
         * otherwise all region of the project will be returned, included **null**.
         */
        regionTypes: readonly RegionTypeEntity[];
    }): ReadonlyArray<null | RegionEntity> {
        const hasDefaultRegionType = where.regionTypes.includes(this.getDefaultRegionType(where.project));
        if (hasDefaultRegionType) {
            where.regionTypes = [null, ...where.regionTypes] as readonly RegionTypeEntity[];
        }
        const q = this.makeListRegionQuery(where);
        const resultSet = q.execute();
        const regions = resultSet.sortedEntities();
        return regions;
    }

    public listDeeplyChildrenRegions(
        project: ProjectEntity,
        parentRegion: RegionEntity | null,
    ): ReadonlyArray<RegionEntity> {
        let regions: ReadonlyArray<RegionEntity> = [];
        if (!parentRegion) {
            regions = project.regions.filter((region) => region.parentRegion === null);
        } else {
            regions = parentRegion.childrenRegions;
        }
        return [...regions, ...regions.flatMap((region) => this.listDeeplyChildrenRegions(project, region))];
    }

    private makeListRegionQuery(where: {
        project: ProjectEntity;
        /**
         * Only the region members of the given regionTypes will be returned,
         * otherwise all region of the project will be returned, included **null**.
         */
        regionTypes?: readonly RegionTypeEntity[];

        ignoreNullRegions?: boolean;
    }): Query<RegionEntity> {
        const { project, regionTypes } = where;
        const entityContext = project.getContext();

        const q = query<RegionEntity>(entityContext, "Region").orderBy("name(natural), id");

        if (regionTypes !== undefined) {
            q.predicateWithFormat("type IN {regionTypes}", { regionTypes });
        }
        return q;
    }

    //#region Region Type

    public listRegionTypes(where: { project: ProjectEntity }): readonly RegionTypeEntity[] {
        const { project } = where;
        const q = query<RegionTypeEntity>(project.getContext(), "RegionType").orderBy(
            "order, templateName(natural), id",
        );
        const resultSet = q.execute();
        const regionTypes = resultSet.sortedEntities();
        return regionTypes;
    }

    public getDefaultRegionType(project: ProjectEntity): RegionTypeEntity {
        const entityContext = project.getContext();
        const q = query<RegionTypeEntity>(entityContext, "RegionType")
            .predicateWithFormat("isDefault == true")
            .orderBy("order, templateName(natural), id");
        const resultSet = q.execute();
        const defaultTypeQuantity = resultSet.count();
        if (defaultTypeQuantity > 1) {
            throw new Error(`There are ${defaultTypeQuantity} default region types in project ${project.name}`);
        }
        const defaultType = resultSet.firstEntity();
        return defaultType as RegionTypeEntity;
    }

    public convertAsRegionTypePayload(regionType: RegionTypeEntity): RegionTypePayload {
        return {
            id: regionType.id,
            name: regionType.name,
            isSystem: regionType.templateId !== null,
        };
    }

    public updateRegionTypes(project: ProjectEntity, payloads: RegionTypePayload[]): void {
        const entityContext = project.getContext();
        const existingRegionTypes = this.listRegionTypes({ project });
        const missingRegionTypes = existingRegionTypes.filter((rt) => !payloads.find((p) => p.id === rt.id));

        missingRegionTypes.forEach((rt) => {
            entityContext.deleteEntity(rt);
        });

        payloads.forEach((payload) => {
            if (payload.id) {
                const regionType = existingRegionTypes.find((rt) => rt.id === payload.id);
                if (!regionType) {
                    throw new Error(`Region type with id ${payload.id} not found in project ${project.name}`);
                }
                regionType.name = payload.name;
            } else {
                createEntityWithProperties(entityContext, "RegionType", {
                    project: project,
                    name: payload.name,
                });
            }
        });
    }

    //#region Region Members

    /**
     * List all region members of the given project.
     */
    public listRegionMembers(where: {
        project: ProjectEntity;
        /**
         * Only the region members of the given regions will be returned,
         * otherwise all region members of the project will be returned.
         */
        regions?: readonly RegionEntity[];
        /**
         * Query-compliant orderBy clause. Defaults to alphabetical member name.
         */
        orderBy?: string;
    }): readonly RegionMemberEntity[] {
        const { project, regions, orderBy = "person.name(natural), id" } = where;

        const q = query<RegionMemberEntity>(project.getContext(), "RegionMember")
            .predicateWithFormat("region.project == {project}", { project })
            /**
             * It should not be possible to create a region member without a
             * person but in the legacy rx-region-controller it is possible.
             *
             * Therefore we have to check if the person is not null.
             */
            .predicateWithFormat("person != null")
            .orderBy(orderBy);

        if (regions) {
            q.predicateWithFormat("region IN {regions}", { regions: regions });
        }

        const resultSet = q.execute();
        const members = resultSet.sortedEntities();

        return members;
    }

    /**
     * Update the given region member entity with the given properties.
     */
    public updateRegionMember(
        regionMember: RegionMemberEntity,
        payload: UpdateEntityPayload<RegionMemberEntity>,
    ): void {
        updateEntityWithProperties(regionMember, payload);
    }

    public deleteRegion(region: RegionEntity): void {
        const entityContext = region.getContext();
        const emptyFormObsMaps = (region.maps ?? [])
            .filter((map) => map.type === MAP_TYPES.FORMS)
            .filter((map) => map.bugs.length === 0);

        emptyFormObsMaps.map((map) => {
            entityContext.deleteEntity(map);
        });
        entityContext.deleteEntity(region);
    }

    //#endregion
}
